import React from 'react';
import { Product } from '@/services/productService';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { pickPrimaryImage } from '@/utils/images';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getCategoryName } from '@/lib/utils';
import { FamilyDisplay } from '@/components/products/FamilyDisplay';
import { useFamilies } from '@/api/familyApi';
import { normalizeFamily } from '@/utils/familyNormalizer';

interface ProductCardProps {
  product: Product;
  onSelect?: (product: Product) => void;
}

export function ProductCard({ product, onSelect }: ProductCardProps) {
  const navigate = useNavigate();
  const { data: families, isLoading: isFamiliesLoading } = useFamilies();

  // Get the primary image using the same utility used in the table
  const imageUrl = pickPrimaryImage(product);

  // Get category name safely - ensure product.category exists before passing it
  const categoryName = product && product.category ? getCategoryName(product.category) : '';

  // Process family data like in the list view
  const processedFamily = React.useMemo(() => {
    // Get family data from the product - might be a family object, ID, or family_name string
    const family = product.family;
    const familyName = product.family_name;

    // First try using family object or ID if available
    if (family) {
      // Normalize the family data for consistent format
      let normalizedFamily = normalizeFamily(family);

      // If we have families data and normalized family shows a generic label ("Family X"),
      // try to find the complete family data from the families list
      if (normalizedFamily && families && families.length > 0 && !isFamiliesLoading) {
        // Check if we need to enhance with full family data
        const hasGenericLabel = normalizedFamily.label.includes(`Family ${normalizedFamily.id}`);
        const isMissingProperties =
          !normalizedFamily.code || normalizedFamily.code === `family-${normalizedFamily.id}`;

        if (hasGenericLabel || isMissingProperties) {
          const fullFamily = families.find(f => f.id === normalizedFamily?.id);
          if (fullFamily) {
            normalizedFamily = normalizeFamily(fullFamily);
          }
        }
      }
      return normalizedFamily;
    }

    // If direct family object isn't available but family_name is, use that
    if (familyName) {
      return { label: familyName };
    }

    // No family data found
    return null;
  }, [product, families, isFamiliesLoading]);

  // Handle click - either call onSelect or navigate
  const handleClick = () => {
    if (onSelect) {
      onSelect(product);
    } else {
      navigate(`/app/products/${product.id}`);
    }
  };

  return (
    <Card
      className="enterprise-card flex h-full cursor-pointer flex-col border-border bg-card transition-all duration-200 hover:shadow-lg"
      onClick={handleClick}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-t-lg">
        <Avatar className="size-full rounded-none">
          <AvatarImage
            src={imageUrl || ''}
            alt={product.name}
            className="size-full bg-muted/50 object-cover"
            loading="lazy"
          />
          <AvatarFallback className="rounded-none bg-muted text-base text-muted-foreground">
            <div className="break-words px-2 py-1 text-center text-xs font-medium leading-tight">
              {product.name || 'Product'}
            </div>
          </AvatarFallback>
        </Avatar>

        {!product.is_active && (
          <Badge variant="secondary" className="enterprise-badge absolute right-2 top-2">
            Inactive
          </Badge>
        )}
      </div>

      <CardContent className="grow p-4 pt-3">
        <h3 className="mb-2 line-clamp-2 text-sm font-semibold text-foreground">{product.name}</h3>
        <p className="mb-1 text-xs text-muted-foreground">SKU: {product.sku}</p>
      </CardContent>

      <CardFooter className="px-3 pb-3 pt-0">
        <div className="flex w-full items-center justify-between">
          <div className="min-w-0 flex-1">
            {categoryName && (
              <p className="truncate text-xs text-muted-foreground">{categoryName}</p>
            )}
          </div>

          <div className="ml-2 shrink-0">
            <FamilyDisplay
              family={processedFamily}
              badgeVariant="secondary"
              showEmpty={true}
              showCode={false}
              hideTooltip={true}
              className="bg-emerald-100 text-emerald-800 transition-colors hover:bg-emerald-200"
            />
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
